"use client";

import React, { useState } from 'react';
import { X, ZoomIn, ChevronLeft, ChevronRight } from 'lucide-react';

const categories = ["All", "Office", "Team", "Events", "Products", "Awards"];

const galleryItems = [
    { id: 1, category: "Office", title: "Nashik HQ – Innovation Hub", image: "/images/unsplash/1497366216548-37526070297c-1200.jpg" },
    { id: 2, category: "Team", title: "Product Engineering Team", image: "/images/unsplash/1522071820081-009f0129c71c-1200.jpg" },
    { id: 3, category: "Events", title: "Auto Expo 2024 – Dubai", image: "/images/unsplash/1540575467063-178a50c2df87-1200.jpg" },
    { id: 4, category: "Products", title: "Autonet DMS Live Dashboard", image: "/images/unsplash/1551288049-bebda4e38f71-1200.jpg" },
    { id: 5, category: "Awards", title: "Best Automotive Software – 2023", image: "/images/unsplash/1567427017947-545c5f8d16ad-1200.jpg" },
    { id: 6, category: "Office", title: "R&D Center – Nashik", image: "/images/unsplash/1497366754035-f200968a6e72-1200.jpg" },
    { id: 7, category: "Team", title: "Customer Support Wing", image: "/images/unsplash/1552664730-d307ca884978-1200.jpg" },
    { id: 8, category: "Events", title: "Dealer Summit – Riyadh 2023", image: "/images/unsplash/1511578314322-379afb476865-1200.jpg" },
    { id: 9, category: "Products", title: "MyPaper – Digital Docs Platform", image: "/images/unsplash/1558655146-d09347e92766-1200.jpg" },
    { id: 10, category: "Awards", title: "CIO Choice Award 2022", image: "/images/unsplash/1606664515524-ed2f786a0bd6-1200.jpg" },
    { id: 11, category: "Events", title: "Annual Dealer Conference 2024", image: "/images/unsplash/1505373877841-8d25f7d46678-1200.jpg" },
    { id: 12, category: "Office", title: "Client Meeting Lounge", image: "/images/unsplash/1497366412874-3415097a27e7-1200.jpg" },
];

const GalleryGrid = () => {
    const [activeCategory, setActiveCategory] = useState("All");
    const [lightbox, setLightbox] = useState(null);

    const filtered = activeCategory === "All"
        ? galleryItems
        : galleryItems.filter(item => item.category === activeCategory);

    const openLightbox = (index) => setLightbox(index);
    const closeLightbox = () => setLightbox(null);
    const prevImage = () => setLightbox((prev) => (prev === 0 ? filtered.length - 1 : prev - 1));
    const nextImage = () => setLightbox((prev) => (prev === filtered.length - 1 ? 0 : prev + 1));

    return (
        <>
            {/* FILTER TABS */}
            <section className="py-10 bg-white border-b border-slate-100 sticky top-[72px] z-40">
                <div className="container mx-auto px-6 md:px-12 lg:px-24 flex gap-3 flex-wrap">
                    {categories.map((cat) => (
                        <button
                            key={cat}
                            onClick={() => setActiveCategory(cat)}
                            className={`px-5 py-2 rounded-xl text-sm font-black transition-all ${activeCategory === cat
                                ? "bg-brand text-white shadow-lg shadow-brand/30"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
            </section>

            {/* GRID */}
            <section className="py-12 md:py-20 bg-slate-50">
                <div className="container mx-auto px-6 md:px-12 lg:px-24">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filtered.map((item, index) => (
                            <div
                                key={item.id}
                                onClick={() => openLightbox(index)}
                                className="group relative rounded-[2rem] overflow-hidden cursor-pointer shadow-md hover:shadow-2xl transition-all duration-500"
                            >
                                <img
                                    src={item.image}
                                    alt={item.title}
                                    className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                <div className="absolute top-4 left-4">
                                    <span className="px-3 py-1 rounded-full bg-brand text-white text-[10px] font-black tracking-wider">
                                        {item.category}
                                    </span>
                                </div>
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white border border-white/30">
                                        <ZoomIn size={24} />
                                    </div>
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                                    <p className="text-white font-black text-lg">{item.title}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {filtered.length === 0 && (
                        <div className="text-center py-24 text-slate-400 font-bold text-xl">
                            No items in this category.
                        </div>
                    )}
                </div>
            </section>

            {/* LIGHTBOX */}
            {lightbox !== null && (
                <div
                    className="fixed inset-0 z-[200] bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={closeLightbox}
                >
                    <button
                        className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
                        onClick={closeLightbox}
                        aria-label="Close image preview"
                    >
                        <X size={24} />
                    </button>

                    <button
                        className="absolute left-4 md:left-8 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-brand transition-colors z-10"
                        onClick={(e) => { e.stopPropagation(); prevImage(); }}
                        aria-label="Previous image"
                    >
                        <ChevronLeft size={24} />
                    </button>

                    <div
                        className="relative max-w-5xl w-full mx-12"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={filtered[lightbox].image}
                            alt={filtered[lightbox].title}
                            className="w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl"
                        />
                        <div className="mt-4 text-center">
                            <span className="px-3 py-1 rounded-full bg-brand text-white text-[10px] font-black tracking-wider mr-2">
                                {filtered[lightbox].category}
                            </span>
                            <span className="text-white font-black text-lg">{filtered[lightbox].title}</span>
                        </div>
                        <p className="text-center text-slate-400 text-sm mt-2">{lightbox + 1} / {filtered.length}</p>
                    </div>

                    <button
                        className="absolute right-4 md:right-8 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-brand transition-colors z-10"
                        onClick={(e) => { e.stopPropagation(); nextImage(); }}
                        aria-label="Next image"
                    >
                        <ChevronRight size={24} />
                    </button>
                </div>
            )}
        </>
    );
};

export default GalleryGrid;
